<template>
  <div>
    <!-- Filters bar -->
    <div class="hr-bar">
      <!-- EA selector (admin) -->
      <div class="hr-field" v-if="role === 'admin'">
        <label>EA</label>
        <select
          class="hr-select"
          v-model="selectedEA"
          @change="loadDailyData"
          style="min-width: 170px"
        >
          <option
            v-for="ea in assistantOptions"
            :key="ea.value"
            :value="ea.value"
            >{{ ea.text }}</option
          >
        </select>
      </div>
      <!-- EA locked (EA role) -->
      <div class="hr-field" v-else>
        <label>EA</label>
        <input
          class="hr-input locked"
          :value="currentEAName"
          readonly
          style="min-width: 170px"
        />
      </div>

      <!-- Day picker -->
      <div class="hr-field" style="position: relative">
        <label>Day</label>
        <button
          class="hr-btn"
          style="min-width: 190px; justify-content: space-between"
          @click="toggleCalendar"
        >
          <span style="display: flex; gap: 7px; align-items: center">
            <b-icon icon="calendar3" font-scale="0.85" />
            {{ dayLabel }}
          </span>
          <b-icon icon="chevron-down" font-scale="0.75" />
        </button>
        <!-- Calendar popup -->
        <div class="hr-pop hr-cal" v-if="calOpen">
          <div class="hr-cal-head">
            <button class="hr-btn hr-btn-sm" style="border: 0" @click="prevCalMonth">
              <b-icon icon="chevron-left" font-scale="0.8" />
            </button>
            <span>{{ calMonthLabel }}</span>
            <button class="hr-btn hr-btn-sm" style="border: 0" @click="nextCalMonth">
              <b-icon icon="chevron-right" font-scale="0.8" />
            </button>
          </div>
          <div class="hr-cal-grid">
            <div v-for="d in dow" :key="d" class="hr-cal-dow">{{ d }}</div>
            <template v-for="(cell, i) in calCells">
              <div v-if="cell === null" :key="'e' + i" />
              <button
                v-else
                :key="'d' + i"
                :class="['hr-cal-day', { on: cell.iso === selectedDay }]"
                @click="pickDay(cell.iso)"
              >
                {{ cell.day }}
                <span v-if="cell.hasData" class="hasdata" />
              </button>
            </template>
          </div>
          <div class="hr-hint" style="margin-top: 10px; padding-left: 2px">
            <span
              style="width: 5px; height: 5px; border-radius: 9px; background: #0d9488; display: inline-block"
            />
            tracked activity
          </div>
        </div>
      </div>

      <!-- Status filter -->
      <div class="hr-field">
        <label>Status</label>
        <select
          class="hr-select"
          v-model="statusFilter"
          style="min-width: 150px"
        >
          <option value="all">All tasks</option>
          <option value="completed">Completed</option>
          <option value="in_progress">In progress</option>
        </select>
      </div>

      <!-- Export CSV -->
      <div class="hr-bar-right" v-if="isAdmin">
        <button class="hr-btn hr-btn-primary" @click="exportCsv">
          <b-icon icon="download" font-scale="0.9" /> Export CSV
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div class="hr-card" v-if="!isLoading && filteredTasks.length === 0">
      <div class="hr-empty">
        <div class="e-ic"><b-icon icon="clock" font-scale="1.2" /></div>
        <div style="font-weight: 700; color: #0f172a; margin-bottom: 4px">
          Nothing tracked this day
        </div>
        <div style="font-size: 13px" v-if="statusFilter === 'all'">
          No timers logged on {{ dayLabel }}. Pick a day with a dot.
        </div>
        <div style="font-size: 13px" v-else>
          No {{
            statusFilter === "completed" ? "completed" : "in-progress"
          }}
          tasks on {{ dayLabel }}.
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="hr-card"
      style="padding: 40px; text-align: center"
    >
      <b-spinner variant="primary" />
      <p class="mt-3" style="color: #64748b">Loading daily report…</p>
    </div>

    <!-- Main content -->
    <template v-if="!isLoading && filteredTasks.length > 0">
      <!-- KPI cards -->
      <div class="hr-kpis">
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="list-task" font-scale="0.9" /> Tasks worked
          </div>
          <div class="k-val">{{ filteredTasks.length }}</div>
          <div class="k-sub">
            {{ customerCount }} customer{{ customerCount > 1 ? "s" : "" }}
          </div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="clock" font-scale="0.9" /> Time on the clock
          </div>
          <div class="k-val">{{ fmtH(totalMinutes) }}</div>
          <div class="k-sub">{{ dayOfWeek }}</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="graph-up" font-scale="0.9" /> Billable
          </div>
          <div class="k-val" style="color: #0d9488">{{ billablePct }}%</div>
          <div class="k-sub">{{ fmtHrs(billableMinutes) }}h billable</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="check-circle" font-scale="0.9" /> First → last
          </div>
          <div class="k-val">{{ activeWindow }}</div>
          <div class="k-sub">active window</div>
        </div>
      </div>

      <!-- Day timeline -->
      <div class="hr-card" style="margin-bottom: 18px; padding: 14px 18px 20px">
        <div class="hr-section-title" style="padding: 0 0 4px">
          Day timeline
        </div>
        <div class="hr-timeline" :style="{ height: timelineLayout.height + 'px' }">
          <div
            v-for="t in timelineBlocks"
            :key="t.id"
            class="hr-tl-block"
            :title="`${t.task} · ${t.customer} · ${fmtH(t.minutes)}`"
            :style="{
              left: t.leftPct + '%',
              width: t.widthPct + '%',
              background: t.color,
              top: 14 + t.rowIndex * 35 + 'px'
            }"
          >
            {{ t.task }}
          </div>
          <div class="hr-tl-axis" :style="{ top: timelineLayout.axisTop + 'px' }" />
          <div
            v-for="h in [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]"
            :key="h"
            class="hr-tl-tick"
            :style="{
              left: (h / 24) * 100 + '%',
              top: timelineLayout.tickTop + 'px'
            }"
          >
            {{ h }}:00
          </div>
        </div>
      </div>

      <!-- Task table -->
      <div class="hr-card">
        <table class="hr-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Customer</th>
              <th>Task</th>
              <th>Category</th>
              <th>Billable</th>
              <th style="text-align: right">Duration</th>
              <th style="text-align: right">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in filteredTasks" :key="t.id">
              <td class="num" style="color: #64748b">
                {{ t.start }}–{{ t.end }}
              </td>
              <td>
                <div style="display: flex; gap: 9px; align-items: center">
                  <span
                    class="hr-avatar"
                    :style="{
                      background: t.avatarColor,
                      border: 0,
                      width: '24px',
                      height: '24px'
                    }"
                    >{{ t.initials }}</span
                  >
                  {{ t.customer }}
                </div>
              </td>
              <td style="font-weight: 600">
                {{ t.task }}
                <span
                  v-if="t.edited"
                  class="hr-badge b-amber"
                  style="margin-left: 8px"
                  >edited</span
                >
              </td>
              <td>
                <span class="hr-badge b-slate">{{ t.category }}</span>
              </td>
              <td>
                <span v-if="t.billable" class="hr-badge b-teal">Yes</span>
                <span v-else class="hr-badge b-slate">No</span>
              </td>
              <td class="num" style="text-align: right; font-weight: 700">
                {{ fmtH(t.minutes) }}
              </td>
              <td style="text-align: right">
                <button
                  v-if="correctionWindowOpen && t.trackerCount <= 1"
                  class="hr-btn hr-btn-sm"
                  @click="openCorrection(t)"
                >
                  <b-icon icon="pencil" font-scale="0.75" /> Correct
                </button>
                <button
                  v-else-if="correctionWindowOpen && t.trackerCount > 1"
                  class="hr-btn hr-btn-sm"
                  v-b-tooltip.hover
                  title="This task ran as several separate timer sessions today - pick which one to correct."
                  @click="openSessionPicker(t)"
                >
                  <b-icon icon="pencil" font-scale="0.75" /> Correct ({{ t.trackerCount }} sessions)
                </button>
                <span
                  v-else
                  class="hr-badge b-slate"
                  v-b-tooltip.hover
                  :title="
                    isAdmin
                      ? 'This period is fully locked past the Wednesday deadline.'
                      : 'This period is locked. Only an Admin can correct it now.'
                  "
                  >Locked</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Session picker: a task with multiple timer sessions in the day
           can't be corrected as one combined total (the backend only ever
           patches a single tracker) - pick which session to correct. -->
      <div class="hr-overlay" v-if="sessionPicker" @click="closeModal">
        <div class="hr-modal" @click.stop>
          <div class="hr-modal-head">
            <span style="width: 34px; height: 34px; border-radius: 9px; background: #fce7ef; display: flex; align-items: center; justify-content: center;">
              <b-icon icon="stopwatch" font-scale="1" style="color: #e11d48" />
            </span>
            <div>
              <div style="font-weight: 700">Choose a session to correct</div>
              <div style="font-size: 12px; color: #94a3b8">
                {{ sessionPicker.task }} · {{ sessionPicker.customer }}
              </div>
            </div>
            <button class="hr-btn hr-btn-sm" style="margin-left: auto; border: 0" @click="closeModal">
              <b-icon icon="x" font-scale="1.1" />
            </button>
          </div>

          <div class="hr-modal-body">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>Session</th>
                  <th style="text-align: right">Duration</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in sessionPicker.sessions" :key="s.id">
                  <td>{{ i + 1 }}. {{ s.range }}</td>
                  <td class="num" style="text-align: right">{{ fmtH(s.minutes) }}</td>
                  <td style="text-align: right">
                    <button class="hr-btn hr-btn-sm" @click="openCorrection(sessionPicker, s)">
                      <b-icon icon="pencil" font-scale="0.75" /> Correct
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Correction Modal -->
      <div class="hr-overlay" v-if="editing" @click="closeModal">
        <div class="hr-modal" @click.stop>
          <div class="hr-modal-head">
            <span style="width: 34px; height: 34px; border-radius: 9px; background: #fce7ef; display: flex; align-items: center; justify-content: center;">
              <b-icon icon="stopwatch" font-scale="1" style="color: #e11d48" />
            </span>
            <div>
              <div style="font-weight: 700">Correct timer</div>
              <div style="font-size: 12px; color: #94a3b8">
                {{ editing.task }} · {{ editing.customer }}
              </div>
            </div>
            <button class="hr-btn hr-btn-sm" style="margin-left: auto; border: 0" @click="closeModal">
              <b-icon icon="x" font-scale="1.1" />
            </button>
          </div>

          <div class="hr-modal-body">
            <div style="display: flex; gap: 22px; margin-bottom: 18px">
              <div>
                <div style="font-size: 11px; color: #94a3b8; font-weight: 600">DURATION</div>
                <div class="num" style="margin-top: 8px; font-weight: 600">{{ fmtH(editing.minutes) }}</div>
              </div>
            </div>

            <div style="font-size: 12px; font-weight: 600; color: #64748b; margin-bottom: 8px">New duration</div>
            <div style="display: flex; align-items: center; gap: 14px">
              <div class="hr-stepper">
                <button @click="stepModal(-5)">
                  <b-icon icon="dash" font-scale="1.1" />
                </button>
                <div class="val">
                  <input
                    type="text"
                    inputmode="numeric"
                    placeholder="h:mm"
                    class="val-input"
                    :value="fmtH(modalVal)"
                    @change="onDurationInput"
                  /><span class="unit">h:mm</span>
                </div>
                <button @click="stepModal(5)">
                  <b-icon icon="plus" font-scale="1.1" />
                </button>
              </div>
              <span v-if="modalDelta !== 0" :class="['hr-badge', modalDelta > 0 ? 'b-teal' : 'b-amber']">
                {{ modalDelta > 0 ? "+" : "" }}{{ modalDelta }} min
              </span>
            </div>

            <div style="font-size: 12px; font-weight: 600; color: #64748b; margin: 18px 0 8px">
              Reason for correction <span style="color: #e11d48">*</span>
            </div>
            <select class="hr-select" style="width: 100%" v-model="modalReason">
              <option value="">Select a reason…</option>
              <option v-for="r in reasons" :key="r">{{ r }}</option>
            </select>

            <div class="hr-confirm">
              <b-icon icon="exclamation-triangle" font-scale="0.9" style="flex-shrink: 0; margin-top: 1px" />
              <span>You're changing this timer from <b>{{ fmtH(editing.minutes) }}</b> to <b>{{ fmtH(modalVal) }}</b>. This affects billing. Are you sure?</span>
            </div>
          </div>

          <div class="hr-modal-foot">
            <button class="hr-btn" @click="closeModal">Cancel</button>
            <span
              v-b-tooltip.hover
              :title="confirmDisabledReason"
            >
              <button class="hr-btn hr-btn-danger" :disabled="!modalReason || !modalDeltaValid" @click="saveCorrection">
                <b-icon icon="check" font-scale="0.9" /> Confirm correction
              </button>
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import { getAssistantsList } from "@/apis/assistants";
import { patchTracker } from "@/apis/common";
import axios from "@/config/axios";
import { mapState } from "vuex";
import {
  TIMER_CORRECTION_REASONS,
  ADMIN_INVOICE_CORRECTION_REASON,
  MIN_CORRECTION_MINUTES
} from "@/utils/constants/index";
import { snapCorrectionMinutes } from "@/utils/utils";

// Color palette for customer avatars
const AVATAR_COLORS = [
  "#0891b2",
  "#db2777",
  "#334155",
  "#7c3aed",
  "#0d9488",
  "#b45309",
  "#2563eb",
  "#0e7490",
  "#c2410c",
  "#059669",
  "#7c2d12",
  "#4338ca"
];

// Color palette for task timeline blocks
const TASK_COLORS = [
  "#06b6d4",
  "#ec4899",
  "#f59e0b",
  "#8b5cf6",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#6366f1",
  "#14b8a6",
  "#f97316",
  "#d946ef",
  "#0ea5e9"
];

export default {
  name: "DailyView",
  data() {
    return {
      selectedDay: moment().format("YYYY-MM-DD"),
      selectedEA: null,
      calOpen: false,
      calMonth: moment().startOf("month"),
      isLoading: false,
      tasks: [],
      statusFilter: "all",
      assistantOptions: [],
      currentEAName: "",
      dataDays: [],
      dow: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      customerColorMap: {},
      colorIndex: 0,
      taskColorMap: {},
      taskColorIndex: 0,
      editing: null,
      sessionPicker: null,
      modalVal: 0,
      modalReason: ""
    };
  },
  computed: {
    ...mapState("authModule", ["role", "profileUserId"]),

    isAdmin() {
      return this.role === "admin";
    },
    reasons() {
      return this.isAdmin
        ? [...TIMER_CORRECTION_REASONS, ADMIN_INVOICE_CORRECTION_REASON]
        : TIMER_CORRECTION_REASONS;
    },
    // EAs can correct the previous reporting period only until the
    // following Monday 12:00 PM UTC; admins are never locked out.
    correctionWindowOpen() {
      // EAs: locked after the following Monday 12:00 PM UTC.
      // Admins: extended until the following Wednesday 12:00 PM UTC.
      const deadlineOffsetDays = this.isAdmin ? 9 : 7;
      const dayMonday = moment.utc(this.selectedDay).startOf("isoWeek");
      const deadline = dayMonday.add(deadlineOffsetDays, "days").add(12, "hours");
      return moment.utc().isSameOrBefore(deadline);
    },
    // modalVal is always kept on the 5-minute grid (see onDurationInput/
    // stepModal), so the only remaining invalid state is "nothing changed".
    modalDeltaValid() {
      return this.modalDelta !== 0;
    },
    confirmDisabledReason() {
      if (!this.modalReason) return "Select a reason for this correction first";
      if (!this.modalDeltaValid) return "Choose a new duration before confirming";
      return "";
    },
    dayLabel() {
      return moment(this.selectedDay).format("ddd, D MMMM");
    },
    dayOfWeek() {
      return moment(this.selectedDay).format("dddd");
    },
    calMonthLabel() {
      return this.calMonth.format("MMMM YYYY");
    },
    calCells() {
      const m = this.calMonth;
      const y = m.year();
      const mo = m.month();
      const first = new Date(y, mo, 1).getDay();
      const offset = (first + 6) % 7; // Mon-first
      const days = new Date(y, mo + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < offset; i++) cells.push(null);
      for (let d = 1; d <= days; d++) {
        const iso = `${y}-${String(mo + 1).padStart(2, "0")}-${String(
          d
        ).padStart(2, "0")}`;
        cells.push({ day: d, iso, hasData: this.dataDays.includes(iso) });
      }
      return cells;
    },
    // Status filter (All / Completed / In progress) applied on top of the
    // day's raw tasks - drives the KPIs, timeline, and table below.
    filteredTasks() {
      if (this.statusFilter === "all") return this.tasks;
      return this.tasks.filter(t =>
        this.statusFilter === "completed" ? t.completed : !t.completed
      );
    },
    totalMinutes() {
      return this.filteredTasks.reduce((a, t) => a + t.minutes, 0);
    },
    billableMinutes() {
      return this.filteredTasks
        .filter(t => t.billable)
        .reduce((a, t) => a + t.minutes, 0);
    },
    billablePct() {
      return this.totalMinutes
        ? Math.round((this.billableMinutes / this.totalMinutes) * 100)
        : 0;
    },
    customerCount() {
      return new Set(this.filteredTasks.map(t => t.customerId)).size;
    },
    activeWindow() {
      if (this.filteredTasks.length === 0) return "—";
      const firstStart = this.filteredTasks[0].start;
      if (firstStart === "—") return "—";
      const lastEnd = this.filteredTasks.reduce((latest, task) => {
        if (latest === "—" || task.end === "—")
          return task.end !== "—" ? task.end : latest;
        const [lh, lm] = latest.split(":").map(Number);
        const [th, tm] = task.end.split(":").map(Number);
        return th * 60 + tm > lh * 60 + lm ? task.end : latest;
      }, this.filteredTasks[0].end);
      return `${firstStart}–${lastEnd}`;
    },
    timelineBlocks() {
      const START = 0; // 0:00
      const SPAN = 1440; // 24 hours
      const blocks = this.filteredTasks.map(t => {
        const [sh, sm] = t.start.split(":").map(Number);
        const [eh, em] = t.end.split(":").map(Number);
        const a = sh * 60 + sm;
        const b = eh * 60 + em;
        return {
          ...t,
          startMins: a,
          endMins: b,
          leftPct: Math.max(0, ((a - START) / SPAN) * 100),
          widthPct: Math.max(3.5, ((b - a) / SPAN) * 100),
          color: this.getColorForTask(t.id),
          rowIndex: 0
        };
      });

      // Detect overlapping blocks and assign row indices for vertical stacking
      blocks.forEach((block, idx) => {
        let maxRow = 0;
        for (let i = 0; i < idx; i++) {
          const prev = blocks[i];
          // Check if blocks overlap
          if (
            block.startMins < prev.endMins &&
            block.endMins > prev.startMins
          ) {
            maxRow = Math.max(maxRow, prev.rowIndex + 1);
          }
        }
        block.rowIndex = maxRow;
      });

      return blocks;
    },
    timelineLayout() {
      const maxRow = this.timelineBlocks.reduce(
        (max, b) => Math.max(max, b.rowIndex),
        0
      );
      const rows = maxRow + 1;
      const axisTop = 14 + rows * 35 + 8;
      const tickTop = axisTop + 4;
      return {
        axisTop,
        tickTop,
        height: Math.max(78, tickTop + 20)
      };
    },
    modalDelta() {
      if (!this.editing) return 0;
      return this.modalVal - this.editing.minutes;
    }
  },
  async mounted() {
    await this.loadAssistants();
    await this.loadDailyData();
  },
  methods: {
    fmtH(m) {
      return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
    },
    fmtHrs(m) {
      return (m / 60).toFixed(1);
    },
    getColorForCustomer(id) {
      if (!this.customerColorMap[id]) {
        this.customerColorMap[id] =
          AVATAR_COLORS[this.colorIndex % AVATAR_COLORS.length];
        this.colorIndex++;
      }
      return this.customerColorMap[id];
    },
    getColorForTask(taskId) {
      if (!this.taskColorMap[taskId]) {
        this.taskColorMap[taskId] =
          TASK_COLORS[this.taskColorIndex % TASK_COLORS.length];
        this.taskColorIndex++;
      }
      return this.taskColorMap[taskId];
    },
    pickDay(iso) {
      this.selectedDay = iso;
      this.calOpen = false;
      this.loadDailyData();
    },
    toggleCalendar() {
      if (!this.calOpen) {
        // Open showing the month the current selection is in.
        this.calMonth = moment(this.selectedDay).startOf("month");
      }
      this.calOpen = !this.calOpen;
    },
    prevCalMonth() {
      this.calMonth = moment(this.calMonth).subtract(1, "month");
      this.loadActivityDatesForMonth();
    },
    nextCalMonth() {
      this.calMonth = moment(this.calMonth).add(1, "month");
      this.loadActivityDatesForMonth();
    },
    async loadAssistants() {
      try {
        const res = await getAssistantsList();
        this.assistantOptions = res.data.results.map(item => ({
          value: item.id,
          text: `${item.user.firstName} ${item.user.lastName}`
        }));
        if (
          (this.role === "admin" || this.role === "owner") &&
          this.assistantOptions.length > 0
        ) {
          // Owners do not log time. Use the first EA on their customers,
          // which is all this list contains for them.
          this.selectedEA = this.assistantOptions[0].value;
          if (this.role === "owner") {
            this.currentEAName = this.assistantOptions[0].text;
          }
        } else if (this.role !== "owner") {
          this.selectedEA = this.profileUserId;
          const me = this.assistantOptions.find(
            a => a.value === this.profileUserId
          );
          this.currentEAName = me ? me.text : "You";
        }
        await this.loadActivityDatesForMonth();
      } catch (e) {
        console.log(e);
      }
    },
    async loadActivityDatesForMonth() {
      if (!this.selectedEA) return;
      try {
        const m = this.calMonth;
        const firstDay = m
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const lastDay = m
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");

        const params = {
          completed_gte: firstDay,
          completed_lte: lastDay,
          assigned_to: this.selectedEA
        };

        const res = await axios({
          url: `/assistant-task-reports/`,
          method: "get",
          params
        });

        const allTasks = res.data.results || res.data || [];

        // Extract unique dates that have activity
        const datesWithActivity = new Set();
        allTasks.forEach(task => {
          if (task.completedDate) {
            datesWithActivity.add(task.completedDate);
          }
        });
        this.dataDays = Array.from(datesWithActivity);
        console.log("Dates with activity (final):", this.dataDays);
      } catch (e) {
        console.log("Error loading activity dates:", e);
        if (e.response) {
          console.log("Error response:", e.response.data);
        }
      }
    },
    async loadDailyData() {
      if (!this.selectedEA) {
        console.warn("selectedEA is not set yet");
        return;
      }
      this.isLoading = true;
      // Reset task colors for new day/EA
      this.taskColorMap = {};
      this.taskColorIndex = 0;
      try {
        const params = {
          completed_gte: this.selectedDay,
          completed_lte: this.selectedDay,
          assigned_to: this.selectedEA
        };
        console.log("Loading daily data with params:", params);
        // Fetch tasks filtered by date and assigned EA
        const res = await axios({
          url: `/assistant-task-reports/`,
          method: "get",
          params
        });
        const rawTasks = res.data.results || res.data || [];

        this.tasks = rawTasks
          .map((item, idx) => {
            const duration = item.roundedDuration || item.duration || 0;
            const minutes = Math.round(duration / 60);
            const customerName = item.createdFor
              ? `${item.createdFor.firstName || ""} ${item.createdFor
                  .lastName || ""}`.trim()
              : item.customerName || "Unknown";
            const customerId = item.createdFor?.id || item.customerId || idx;
            const initials = customerName
              .split(" ")
              .map(w => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();

            // Parse start/end times - extract from trackers array
            let start = "—";
            let end = "—";
            let trackerId = null;
            let startAt = null;
            // A task can have multiple start/stop timer sessions in a day -
            // `minutes` above sums all of them, but a correction can only
            // ever patch one tracker's own stop_at (see saveCorrection), so
            // each session needs its own start/duration kept around to
            // correct individually (see openSessionPicker).
            let trackerCount = 0;
            let sessions = [];

            if (item.trackers && item.trackers.length > 0) {
              // Extract from trackers array (startAt/stopAt are in ISO format)
              const firstTracker = item.trackers[0];
              const lastTracker = item.trackers[item.trackers.length - 1];
              if (firstTracker?.startAt) {
                start = moment(firstTracker.startAt).format("HH:mm");
              }
              if (lastTracker?.stopAt) {
                end = moment(lastTracker.stopAt).format("HH:mm");
              }
              trackerId = firstTracker?.id || null;
              startAt = firstTracker?.startAt || null;
              trackerCount = item.trackers.length;
              sessions = item.trackers.map(tr => ({
                id: tr.id,
                startAt: tr.startAt,
                range: `${moment(tr.startAt).format("HH:mm")}–${
                  tr.stopAt ? moment(tr.stopAt).format("HH:mm") : "running"
                }`,
                minutes: Math.round((tr.roundedDuration || tr.duration || 0) / 60)
              }));
            } else if (item.startedAt && item.completedAt) {
              start = moment(item.startedAt).format("HH:mm");
              end = moment(item.completedAt).format("HH:mm");
            } else if (item.start && item.end) {
              start = item.start;
              end = item.end;
            }

            return {
              id: item.id || idx,
              taskId: item.id,
              trackerId,
              trackerCount,
              sessions,
              startAt,
              start,
              end,
              customer: customerName,
              customerId,
              initials,
              avatarColor: this.getColorForCustomer(customerId),
              task: item.title || item.task || "Task",
              category: item.category?.title || item.categoryName || "General",
              billable: item.isBillable !== undefined ? item.isBillable : true,
              minutes,
              edited: !!item.isEdited,
              completed: !!item.completedDate
            };
          })
          .sort((a, b) => {
            const [ah, am] = a.start.split(":").map(Number);
            const [bh, bm] = b.start.split(":").map(Number);
            return ah * 60 + am - (bh * 60 + bm);
          });

        // Track which days have data (for calendar dots)
        if (
          !this.dataDays.includes(this.selectedDay) &&
          this.tasks.length > 0
        ) {
          this.dataDays.push(this.selectedDay);
        }
      } catch (e) {
        console.log("Error loading daily data:", e);
        if (e.response) {
          console.log("Response status:", e.response.status);
          console.log("Response data:", e.response.data);
        }
        this.tasks = [];
      } finally {
        this.isLoading = false;
      }
    },
    exportCsv() {
      const eaName =
        this.role === "admin"
          ? this.assistantOptions.find(a => a.value === this.selectedEA)
              ?.text || "EA"
          : this.currentEAName;
      const head = [
        "EA",
        "Date",
        "Start",
        "End",
        "Customer",
        "Task",
        "Category",
        "Billable",
        "Minutes"
      ];
      const rows = this.filteredTasks.map(t => [
        eaName,
        this.selectedDay,
        t.start,
        t.end,
        t.customer,
        t.task,
        t.category,
        t.billable ? "Yes" : "No",
        t.minutes
      ]);
      const csv = [head, ...rows]
        .map(r =>
          r
            .map(c => {
              const v = String(c ?? "");
              return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
            })
            .join(",")
        )
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `headroom_daily_${this.selectedDay}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$emit("notify", "CSV downloaded");
    },
    openSessionPicker(task) {
      this.sessionPicker = task;
    },
    // `session`, when given, is one of task.sessions (a single tracker) -
    // editing must target that session's own id/start/duration, not the
    // task-day row's combined totals, or the correction silently misfires
    // (see the trackerCount comment above where sessions is built).
    openCorrection(task, session = null) {
      this.editing = {
        ...task,
        trackerId: session ? session.id : task.trackerId,
        startAt: session ? session.startAt : task.startAt,
        minutes: session ? session.minutes : task.minutes
      };
      this.modalVal = this.editing.minutes;
      this.modalReason = "";
      this.sessionPicker = null;
    },
    // "1:30" / "90" / "" -> total minutes. Anything unparseable falls back
    // to the current value instead of silently zeroing the field out.
    parseHHMM(str) {
      const parts = String(str).trim().split(":");
      if (parts.length === 2) {
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        return h * 60 + m;
      }
      const n = parseInt(str, 10);
      return isNaN(n) ? this.modalVal : n;
    },
    // The corrected total must land on a multiple of 5 minutes (the backend
    // enforces this) - snap on typed input, and on every +/- stepper click
    // too, so a non-multiple recording still lands clean instead of just
    // drifting by the raw step size.
    onDurationInput(e) {
      this.modalVal = snapCorrectionMinutes(
        this.parseHHMM(e.target.value),
        MIN_CORRECTION_MINUTES
      );
    },
    stepModal(delta) {
      this.modalVal = snapCorrectionMinutes(
        this.modalVal + delta,
        MIN_CORRECTION_MINUTES
      );
    },
    closeModal() {
      this.editing = null;
      this.sessionPicker = null;
    },
    async saveCorrection() {
      if (!this.editing) return;
      if (!this.editing.taskId || !this.editing.trackerId || !this.editing.startAt) {
        console.error("Missing task/tracker info, cannot correct this timer:", this.editing);
        this.$emit("notify", "Unable to correct this timer (missing tracker data)");
        this.closeModal();
        return;
      }
      this.isLoading = true;
      try {
        // Use .utc() before formatting - moment's [Z] format token is a
        // literal, not a real UTC conversion, so without .utc() this sends
        // local-time values mislabeled as UTC (silently shifting the time
        // by the browser's UTC offset).
        const stopAt = moment
          .utc(this.editing.startAt)
          .add(this.modalVal, "minutes")
          .format("YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]");

        const payload = {
          action: "correction",
          stopAt: stopAt,
          reason: this.modalReason
        };
        await patchTracker(this.editing.taskId, this.editing.trackerId, payload);
        this.closeModal();
        this.$emit("notify", "Timer corrected");
        await this.loadDailyData();
      } catch (err) {
        console.error("Error saving timer correction:", err);
        this.$emit(
          "notify",
          "Error: " + (err.response?.data?.detail || err.message)
        );
      } finally {
        this.isLoading = false;
      }
    }
  },
  watch: {
    selectedEA() {
      // Reload activity dates if EA changed
      this.loadActivityDatesForMonth();
    }
  }
};
</script>
